/* Fever Collective — theme behaviour
 *
 * Replaces the inline onclick handlers the static prototype used
 * (age gate, region simulator, chip filtering) with delegated
 * listeners, plus the cart interactions the prototype faked.
 * Depends on nothing. Translation is Shopify's, server-side: text arrives
 * already localized, so there is nothing for this file to translate.
 */
(function () {
  'use strict';

  /* There is deliberately no formatMoney here.
   *
   * Under Shopify Markets the same variant is a different number in a
   * different currency for every buyer, and only Liquid knows which —
   * it renders inside the market context. Anything this file formats
   * client-side is formatted from the shop's currency, so the price
   * would change the instant a buyer touched the variant select and
   * then disagree with checkout. Every money string is therefore
   * server-rendered and handed to us: see the VariantData blob in
   * sections/main-product.liquid, and renderSection() below, which
   * re-renders the whole cart from the server rather than doing sums.
   */

  /* ---------- Age gate ----------
   * The prototype re-prompted on every page load. Persist the
   * answer so it asks once per browser instead.
   */
  function initAgeGate() {
    var gate = document.getElementById('ageGate');
    if (!gate) return;

    var KEY = 'fc_age_ok';
    var ttlDays = parseInt(gate.dataset.ttlDays || '30', 10);
    var stored = null;
    try { stored = localStorage.getItem(KEY); } catch (e) { /* private mode */ }

    if (stored && (Date.now() - parseInt(stored, 10)) < ttlDays * 864e5) {
      gate.remove();
      return;
    }

    gate.hidden = false;
    document.body.style.overflow = 'hidden';

    gate.addEventListener('click', function (e) {
      var enter = e.target.closest('[data-age-enter]');
      var exit = e.target.closest('[data-age-exit]');
      if (enter) {
        try { localStorage.setItem(KEY, String(Date.now())); } catch (err) {}
        gate.remove();
        document.body.style.overflow = '';
      } else if (exit) {
        window.location.href = exit.dataset.ageExit || 'https://www.google.com';
      }
    });
  }

  /* ---------- Mobile nav ----------
   * The panel is in-flow, not an overlay, so it needs no focus trap and
   * no body scroll lock -- but it does need the three ways people
   * expect to dismiss it: the button, Escape, and a tap outside. */
  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var panel = document.getElementById('mobileNav');
    if (!toggle || !panel) return;

    var header = toggle.closest('.site-header') || panel.parentNode;

    function setOpen(open) {
      panel.classList.toggle('open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    function isOpen() {
      return panel.classList.contains('open');
    }

    toggle.addEventListener('click', function () {
      setOpen(!isOpen());
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) {
        setOpen(false);
        toggle.focus();
      }
    });

    document.addEventListener('click', function (e) {
      if (isOpen() && !header.contains(e.target)) setOpen(false);
    });

    /* Above 900px the panel's contents live in the bar again, so a
     * panel left open would duplicate them. */
    window.addEventListener('resize', function () {
      if (isOpen() && window.innerWidth > 900) setOpen(false);
    });
  }

  /* ---------- Product gallery ---------- */
  function initGallery() {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-gallery-main]');
    if (!main) return;
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    var current = 0;

    function show(index) {
      var n = thumbs.length;
      if (!n) return;
      current = (index % n + n) % n;
      var thumb = thumbs[current];
      // Rebuild the element rather than writing markup, so the responsive
      // candidate set the thumb carries survives the swap. Writing a bare
      // src here would drop the srcset the first paint had.
      var img = document.createElement('img');
      img.alt = thumb.dataset.alt || '';
      if (thumb.dataset.srcset) {
        img.srcset = thumb.dataset.srcset;
        img.sizes = thumb.dataset.sizes || '(min-width: 900px) 45vw, 100vw';
      }
      img.src = thumb.dataset.full;
      main.innerHTML = '';
      main.appendChild(img);
      thumbs.forEach(function (t) {
        t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
      });
    }

    gallery.addEventListener('click', function (e) {
      if (e.target.closest('[data-gallery-prev]')) return show(current - 1);
      if (e.target.closest('[data-gallery-next]')) return show(current + 1);
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (thumb) show(thumbs.indexOf(thumb));
    });

    // Horizontal swipe on the main image; vertical drags stay page scrolls
    // (touch-action: pan-y in CSS).
    var startX = null, startY = null;
    main.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    main.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      startX = startY = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(current + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }

  /* ---------- Variant picker ---------- */
  function initVariants() {
    var form = document.querySelector('[data-product-form]');
    if (!form) return;
    var select = form.querySelector('[data-variant-select]');
    if (!select) return;

    var priceEl = document.querySelector('[data-product-price]');
    var compareEl = document.querySelector('[data-product-compare]');
    // The struck-through figure ships wrapped in a "Regular" group and
    // sits beside a PRESALE badge. All three describe the same fact, so
    // a variant change has to show or hide them together — toggling the
    // <s> alone would leave a stray "Regular" word and a badge behind.
    var compareGroupEl = document.querySelector('[data-product-compare-group]');
    var presaleBadgeEl = document.querySelector('[data-presale-badge]');
    var submit = form.querySelector('[data-add-to-cart]');
    var data = {};
    try {
      data = JSON.parse(document.getElementById('VariantData').textContent);
    } catch (e) { return; }

    select.addEventListener('change', function () {
      var v = data[select.value];
      if (!v) return;
      // price_money / compare_at_price_money are rendered by Liquid in the
      // buyer's market currency. The _with_code variants carry the ISO code
      // ("$118.88 USD"); the price node says which form it was built with,
      // so a variant change cannot make the code appear or vanish.
      var withCode = priceEl && priceEl.hasAttribute('data-with-code');
      var priceText = withCode ? v.price_money_with_code : v.price_money;
      var compareText = withCode ? v.compare_at_price_money_with_code : v.compare_at_price_money;
      if (priceEl) priceEl.textContent = priceText;

      var onSale = !!(v.compare_at_price && v.compare_at_price > v.price);
      if (compareEl && onSale) compareEl.textContent = compareText;
      if (compareGroupEl) compareGroupEl.hidden = !onSale;
      else if (compareEl) compareEl.hidden = !onSale;
      if (presaleBadgeEl) presaleBadgeEl.hidden = !onSale;
      if (priceEl) priceEl.classList.toggle('on-sale', onSale);
      if (submit) {
        submit.disabled = !v.available;
        submit.textContent = v.available
          ? submit.dataset.labelAvailable + ' — ' + v.price_money
          : submit.dataset.labelSoldOut;
      }
      var url = new URL(window.location.href);
      url.searchParams.set('variant', select.value);
      window.history.replaceState({}, '', url.toString());
    });
  }

  /* ---------- Cart ---------- */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function addToCart(items, button) {
    var original = button ? button.textContent : null;
    if (button) { button.disabled = true; button.textContent = button.dataset.labelAdding || 'Adding…'; }

    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.body.description || res.body.message || 'Could not add to cart');
        return refreshCart();
      })
      .then(function () {
        /* The page no longer navigates away, so the button has to be
         * handed back to the shopper itself. */
        if (button) { button.disabled = false; button.textContent = original; }
        openDrawer(button);
      })
      .catch(function (err) {
        var target = button ? button.closest('form, .bundle-box') : document.body;
        var slot = target && target.querySelector('[data-form-error]');
        if (slot) { slot.textContent = err.message; slot.hidden = false; }
        else { alert(err.message); }
        if (button) { button.disabled = false; button.textContent = original; }
      });
  }

  function initAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      var id = form.querySelector('[name="id"]').value;
      var qty = form.querySelector('[name="quantity"]');
      addToCart([{ id: Number(id), quantity: qty ? Number(qty.value) : 1 }], form.querySelector('[data-add-to-cart]'));
    });

    /* "Complete the collection" — adds every variant in one request.
     * The 10% saving itself is an automatic discount configured in
     * Shopify admin; the theme only presents the offer. */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bundle-add]');
      if (!btn) return;
      e.preventDefault();
      var ids = JSON.parse(btn.dataset.bundleAdd || '[]');
      if (!ids.length) return;
      addToCart(ids.map(function (id) { return { id: Number(id), quantity: 1 }; }), btn);
    });
  }

  /* ---------- Cart line items ----------
   * Lines are addressed by `key`, not by index: an index shifts the
   * moment any other line is removed, and the drawer and the cart page
   * can both be looking at the same cart.
   */
  var cartBusy = false;

  function changeLine(key, quantity) {
    if (cartBusy) return Promise.resolve();
    cartBusy = true;

    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        return r.json().then(function (data) {
          /* Shopify's add-to-cart limit either rejects the change (422 with
           * a description) or clamps it silently and answers 200 with the
           * lower quantity. Either way the shopper needs telling, or the
           * stepper just snaps back with no explanation. */
          var limited = false;
          if (!r.ok) {
            /* Shopify's own 422 wording ("Only 5 items were added to your
             * cart due to availability") blames stock rather than the
             * limit, so it is replaced with the theme's message. */
            if (r.status !== 422) throw new Error('cart/change failed');
            limited = true;
          } else if (quantity > 0) {
            var line = (data.items || []).filter(function (i) { return i.key === key; })[0];
            if (line && line.quantity < quantity) limited = true;
          }
          return refreshCart().then(function () {
            if (!limited) return;
            /* The re-rendered input holds the quantity the cart kept,
             * which is the limit. */
            var input = document.querySelector('[data-cart-qty="' + CSS.escape(key) + '"]');
            var kept = input ? parseInt(input.value, 10) : NaN;
            if (kept > 0) showLineError(key, limitMessage(kept));
          });
        });
      })
      .catch(function () { window.location.reload(); })
      .then(function () { cartBusy = false; });
  }

  function limitMessage(limit) {
    var s = window.FCTheme && window.FCTheme.strings && window.FCTheme.strings.quantityLimit;
    return s ? s.replace('__LIMIT__', limit) : 'You can only add ' + limit + ' of this item to your cart.';
  }

  /* Runs after the drawer re-render, so the message lands in the fresh
   * markup; the next re-render clears it on its own. */
  function showLineError(key, message) {
    var input = document.querySelector('[data-cart-qty="' + CSS.escape(key) + '"]');
    var controls = input && input.closest('.cart-drawer__controls');
    if (!controls) { alert(message); return; }
    var p = document.createElement('p');
    p.className = 'cart-line-error';
    p.setAttribute('role', 'status');
    p.textContent = message;
    controls.insertAdjacentElement('afterend', p);
  }

  /* Re-render every cart surface currently on screen from the server,
   * so money formatting and discounts stay Liquid's job. */
  function refreshCart() {
    var jobs = [
      fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { updateCartCount(cart.item_count); })
    ];

    if (document.querySelector('[data-cart-drawer]')) {
      jobs.push(
        renderSection(window.Shopify.routes.root, 'cart-drawer', '[data-cart-drawer]')
          .then(function () {
            /* The panel element the swap replaced may have held focus;
             * put it back inside the dialog so the trap still has a hold. */
            if (!isDrawerOpen()) return;
            var panel = drawerEl().querySelector('.cart-drawer__panel');
            if (panel && !panel.contains(document.activeElement)) panel.focus();
          })
      );
    }
    /* There is no cart page to re-render any more -- /cart redirects to the
     * drawer (sections/main-cart-redirect.liquid), so the drawer above is the
     * only cart surface that can be on screen.
     */

    return Promise.all(jobs);
  }

  /* Published so anything that changes the cart from outside this file can
     have the drawer and the cart section re-rendered without a full page
     reload, which would close the drawer. The remote-area postcode widget was
     the original caller; nothing uses it today, but it is the only supported
     way back in and costs two lines. */
  window.FCCart = window.FCCart || {};
  window.FCCart.refresh = refreshCart;

  function renderSection(url, section, selector) {
    return fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'sections=' + section)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var target = document.querySelector(selector);
        var html = data[section];
        if (!target || !html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector(selector);
        if (fresh) target.innerHTML = fresh.innerHTML;
      });
  }

  function initCartPage() {
    document.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-cart-remove]');
      if (!remove) return;
      e.preventDefault();
      changeLine(remove.dataset.cartRemove, 0);
    });

    document.addEventListener('change', function (e) {
      var qty = e.target.closest('[data-cart-qty]');
      if (!qty) return;
      var value = Math.max(0, parseInt(qty.value, 10) || 0);
      changeLine(qty.dataset.cartQty, value);
    });

    /* − / + stepper buttons in the drawer. */
    document.addEventListener('click', function (e) {
      var step = e.target.closest('[data-qty-step]');
      if (!step) return;
      e.preventDefault();
      var input = step.parentNode.querySelector('[data-cart-qty]');
      if (!input) return;
      var next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyStep));
      input.value = next;
      changeLine(input.dataset.cartQty, next);
    });
  }

  /* ---------- Cart drawer ----------
   * A real modal, unlike the in-flow mobile nav: it locks the page
   * behind it (the age gate's technique) and traps focus.
   */
  var drawerReturnFocus = null;

  function drawerEl() { return document.querySelector('[data-cart-drawer]'); }

  function openDrawer(invoker) {
    var el = drawerEl();
    if (!el) return;
    drawerReturnFocus = invoker || document.activeElement;
    el.hidden = false;
    /* Let the browser paint the hidden->shown state before transitioning. */
    requestAnimationFrame(function () { el.classList.add('open'); });
    document.body.style.overflow = 'hidden';
    var panel = el.querySelector('.cart-drawer__panel');
    if (panel) panel.focus();
  }

  function closeDrawer() {
    var el = drawerEl();
    if (!el || el.hidden) return;
    el.classList.remove('open');
    document.body.style.overflow = '';

    var done = function () { el.hidden = true; };
    var panel = el.querySelector('.cart-drawer__panel');
    if (panel && getComputedStyle(panel).transitionDuration !== '0s') {
      panel.addEventListener('transitionend', done, { once: true });
    } else {
      done();
    }

    if (drawerReturnFocus && document.contains(drawerReturnFocus)) drawerReturnFocus.focus();
    drawerReturnFocus = null;
  }

  function isDrawerOpen() {
    var el = drawerEl();
    return !!el && !el.hidden;
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initCartDrawer() {
    /* Somebody who asked for /cart gets the drawer instead. The retired cart
     * template bounced them back here and left this flag behind; it is read
     * once and cleared, so a later reload of the page they landed on does
     * not pop the drawer open again. See sections/main-cart-redirect.liquid.
     */
    try {
      if (sessionStorage.getItem('fc:open-cart-drawer')) {
        sessionStorage.removeItem('fc:open-cart-drawer');
        if (drawerEl()) openDrawer(null);
      }
    } catch (e) {}

    /* Cart links open the drawer rather than navigating. They stay real
     * <a href="/cart"> elements, so middle-click, ⌘/Ctrl-click and a
     * missing drawer (or no JS) all still reach /cart -- which redirects
     * straight back here with the flag above set.
     */
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-cart-drawer-open]');
      if (!opener) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (!drawerEl()) return;
      e.preventDefault();
      if (isDrawerOpen()) closeDrawer();
      else openDrawer(opener);
    });

    document.addEventListener('click', function (e) {
      if (!isDrawerOpen()) return;
      if (e.target.closest('[data-cart-drawer-close]')) {
        e.preventDefault();
        closeDrawer();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (!isDrawerOpen()) return;

      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab') return;

      var panel = drawerEl().querySelector('.cart-drawer__panel');
      var items = Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        function (n) { return n.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------- Hero media slider ----------
   * Fades between image and video slides. Only the active slide's
   * video plays; off-screen embeds keep their src unset until first
   * shown so nothing downloads a video the visitor never reaches.
   */
  function initHeroSlider(root) {
    var sliders = (root || document).querySelectorAll('[data-hero-slider]');
    if (!sliders.length) return;

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function playMedia(slide) {
      if (!slide) return;
      var video = slide.querySelector('video');
      if (video) {
        video.muted = true;
        var p = video.play();
        if (p && p.catch) p.catch(function () { /* autoplay blocked by the browser */ });
      }
      var frame = slide.querySelector('[data-hero-embed]');
      if (frame && !frame.getAttribute('src')) frame.setAttribute('src', frame.dataset.heroEmbed);
    }

    function stopMedia(slide) {
      if (!slide) return;
      var video = slide.querySelector('video');
      if (video) video.pause();
    }

    Array.prototype.forEach.call(sliders, function (slider) {
      var slides = slider.querySelectorAll('[data-hero-slide]');
      if (slides.length < 2) {
        playMedia(slides[0]);
        return;
      }

      var dots = slider.querySelectorAll('[data-hero-dot]');
      var index = 0;
      var timer = null;
      var interval = parseInt(slider.dataset.interval, 10) || 6000;
      var autoplay = slider.dataset.autoplay === 'true' && !reduced;

      // A slide may carry a click-through link. It has to leave the tab
      // order whenever its slide goes aria-hidden, or the keyboard walks
      // into a link nobody can see.
      function setLinkFocusable(slide, focusable) {
        var link = slide.querySelector('.hero-slide-link');
        if (!link) return;
        if (focusable) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', '-1');
      }

      function show(next) {
        next = (next + slides.length) % slides.length;
        if (next === index) return;

        stopMedia(slides[index]);
        slides[index].classList.remove('is-active');
        slides[index].setAttribute('aria-hidden', 'true');
        setLinkFocusable(slides[index], false);

        index = next;
        slides[index].classList.add('is-active');
        slides[index].removeAttribute('aria-hidden');
        setLinkFocusable(slides[index], true);
        playMedia(slides[index]);

        Array.prototype.forEach.call(dots, function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
      }

      function start() {
        if (!autoplay || timer) return;
        timer = setInterval(function () { show(index + 1); }, interval);
      }

      function stop() {
        if (!timer) return;
        clearInterval(timer);
        timer = null;
      }

      /* A manual move restarts the clock rather than firing mid-interval. */
      function move(step) {
        show(index + step);
        if (timer) { stop(); start(); }
      }

      var prev = slider.querySelector('[data-hero-prev]');
      var next = slider.querySelector('[data-hero-next]');
      if (prev) prev.addEventListener('click', function () { move(-1); });
      if (next) next.addEventListener('click', function () { move(1); });

      Array.prototype.forEach.call(dots, function (dot) {
        dot.addEventListener('click', function () {
          show(Number(dot.dataset.heroDot));
          if (timer) { stop(); start(); }
        });
      });

      slider.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
      });

      /* Horizontal swipe on touch screens. Vertical drags fall through to page scroll. */
      var touchX = null;
      var touchY = null;
      slider.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { touchX = null; return; }
        touchX = e.touches[0].clientX;
        touchY = e.touches[0].clientY;
        stop();
      }, { passive: true });
      slider.addEventListener('touchend', function (e) {
        if (touchX === null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        var dy = e.changedTouches[0].clientY - touchY;
        touchX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) move(dx < 0 ? 1 : -1);
        start();
      }, { passive: true });
      slider.addEventListener('touchcancel', function () { touchX = null; start(); }, { passive: true });

      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      playMedia(slides[0]);
      start();
    });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initAgeGate();
    initNav();
    initGallery();
    initVariants();
    initAddToCart();
    initCartPage();
    initCartDrawer();
    initHeroSlider();
  });

  /* The theme editor re-renders a section on every settings change. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector) initHeroSlider(e.target);
  });
})();
